/**
 * PIN Lock Context
 *
 * Provides global PIN lock state management across the app.
 * Wraps the app and shows PIN lock screen when locked.
 *
 * TWO SCREENS:
 * 1. StationLoginScreen - Shows on app open when no barber selected (PIN identifies who)
 * 2. PinLockScreen - Shows on inactivity lock (already know who, just verify PIN)
 *
 * Usage:
 * 1. Wrap your app with <PinLockProvider>
 * 2. On app open, if no barber selected, StationLoginScreen shows
 * 3. Barber enters PIN to identify themselves
 * 4. If inactivity lock triggers, PinLockScreen shows
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { pinLockService, BarberPinSettings } from '../services/pinLockService';
import PinLockScreen from '../components/auth/PinLockScreen';
import StationLoginScreen from '../components/auth/StationLoginScreen';
import { Barber, barberDataService } from '../services/barberDataService';
import { endOperatorSession } from '../services/operatorSession';
import { restoreOperatorSession } from '../services/barberAuthService';
import { initBarberPush, clearBarberPush } from '../services/barberPushService';
import { stationService } from '../services/stationService';
import { useAuth } from './AuthContext';
import { shopSettingsService } from '../services/shopSettingsService';

// OPERATOR PIN — enforced on STAGING only for now (ships to prod in the one-shot with the gate).
// So prod is unaffected: no PIN pad, opens straight through, exactly as it does today. (2026-09-24)
const OPERATOR_PIN_ENFORCED = true; // operator PIN model enforced everywhere (prod one-shot 2026-09-26)
const IDLE_LOGOUT_MS = 60_000; // DEFAULT: ~1 min no movement → PIN pad. Overridden live by the shop's
                               // Settings → Stations → Station Lock (0 = off, manual lock only).

interface PinLockContextType {
  isLocked: boolean;
  currentBarber: BarberPinSettings | null;
  requiresStationLogin: boolean; // True when no barber selected (show PIN pad on open)
  setBarber: (barber: BarberPinSettings | null) => void;
  loadBarberById: (barberId: string) => Promise<void>;
  lock: () => void;
  unlock: () => void;
  recordActivity: () => void;
  signOutStation: () => void; // Sign out of station (back to PIN screen)
  promptForPay: () => void; // Surface the operator PIN pad to re-elevate for pay (Jose 2026-11-04)
}

const PinLockContext = createContext<PinLockContextType | undefined>(undefined);

interface PinLockProviderProps {
  children: ReactNode;
  onLogout?: () => void;
}

const STATION_BARBER_KEY = 'slikpos_station_barber';

export const PinLockProvider: React.FC<PinLockProviderProps> = ({ children, onLogout }) => {
  const [isLocked, setIsLocked] = useState(false);
  const [currentBarber, setCurrentBarber] = useState<BarberPinSettings | null>(null);
  // NO PIN-PAD FIRST SCREEN (Jose 2026-09-23): the StationLoginScreen was a clock-in / "start your
  // shift" gate; that purpose was removed 2026-09-01 (see handleStationLogin note) so a blocking PIN
  // pad on app open has no use — it just stalls barbers (their own screenshot showed "0 PINs
  // configured / device Unassigned"). Default to NOT requiring it; the app opens straight to the
  // normal email login / app. PIN identity still works for anyone who sets one (BarberStationIndicator).
  const [requiresStationLogin, setRequiresStationLogin] = useState(false);
  // "Tap in to manage pay" (Jose 2026-11-04): when a pay surface is locked (operator session dropped / not
  // elevated), force the operator PIN pad even though an operator is already set, so they can re-elevate.
  const [forcePinPad, setForcePinPad] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  // Auto-lock timeout (ms). Configurable in Settings → Stations → Station Lock. 0 = off. (2026-09-24)
  const [autoLockMs, setAutoLockMs] = useState<number>(IDLE_LOGOUT_MS);
  const { user } = useAuth();

  // The SHARED shop/register account is role 'staff' — it needs a PIN operator (who's transacting).
  // An owner/manager logged in as THEMSELVES already IS the operator, so they skip the PIN pad.
  const isSharedShopAccount = ((user as any)?.role || '') === 'staff';

  // Restore a previously-chosen station barber (operator identity) on mount.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STATION_BARBER_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // BEHAVIOR-PIN (2026-09-29): migrate any legacy stored raw PIN out of localStorage — we no
        // longer cache raw pins on the client. Keep only the hasPin flag.
        if (parsed && typeof parsed === 'object') {
          if ('pin' in parsed) { parsed.hasPin = parsed.hasPin ?? !!parsed.pin; delete parsed.pin; }
          parsed.hasPin = parsed.hasPin ?? true;
          localStorage.setItem(STATION_BARBER_KEY, JSON.stringify(parsed));
        }
        setCurrentBarber(parsed);
        pinLockService.setCurrentBarber(parsed);
        console.log('[PinLock] Restored station barber:', parsed.barberName);
        // OPERATOR RE-ELEVATION ON RELOAD (Jose 2026-11-04): the operator session (App2 custom-token auth) is
        // in-memory only, so after a reload/app-update it's GONE — and owner/manager pay access silently falls
        // back to the shared STAFF account (shows a fake 40% / pay "reverts" / "didn't save"). Re-establish it
        // WITHOUT a PIN for ANY stored operator; the server (restoreOperatorSession CF) decides eligibility —
        // it re-mints only for a FRESH device-bound tap-in or an explicit stay-signed-in opt-in, so this can't
        // escalate a barber into an owner. Best-effort; if denied, the "Tap in to manage pay" prompt covers it.
        const bId = (parsed as any).id || (parsed as any).barberId;
        if (bId) restoreOperatorSession(bId).catch(() => { /* server denied → PIN tap-in re-elevates */ });
        // Re-tag this device's push token to the restored barber (silent, only if already granted).
        if ((parsed as any)?.barberId) initBarberPush((parsed as any).barberId, (parsed as any).barberName).catch(() => {});
      }
    } catch (err) {
      console.error('[PinLock] Error loading station barber:', err);
    }
    setIsInitialized(true);
  }, []);

  // SECURITY (Phase B 2026-09-30): when the operator clears (idle-lock / sign-out / switch to the PIN
  // pad), tear down the ephemeral operator session so its scoped pay-read token is gone. A fresh tap-in
  // (verifyBarberPin) starts a new one. On reload the session is gone (in-memory) → re-established when
  // the operator taps back in past the lock.
  useEffect(() => {
    if (!currentBarber) { endOperatorSession().catch(() => {}); }
  }, [currentBarber]);

  // DERIVED: show the PIN pad (operator sign-in) only on the shared shop account with no operator yet,
  // and only where enforced (staging for now). Single source of truth → no stale/blocking pad.
  useEffect(() => {
    if (!isInitialized) return;
    // forcePinPad → show the pad even with an operator set (re-elevate to manage pay).
    setRequiresStationLogin(OPERATOR_PIN_ENFORCED && isSharedShopAccount && (!currentBarber || forcePinPad));
  }, [isInitialized, isSharedShopAccount, currentBarber, forcePinPad]);

  // Live station-lock timeout from Settings (Stations → Station Lock). 0 min = OFF (manual lock only).
  useEffect(() => {
    const unsub = shopSettingsService.subscribe((s) => {
      const mins = s.stationLock?.autoLockMinutes;
      setAutoLockMs(typeof mins === 'number' ? Math.max(0, mins) * 60_000 : IDLE_LOGOUT_MS);
    });
    return () => unsub();
  }, []);

  // IDLE-LOCK: after the configured idle time with no movement, drop the operator so the next person
  // taps in (Jose's "goes back to the PIN pad" — only while an operator is active on the shared
  // account). autoLockMs === 0 → OFF (rely on the manual Lock Screen). (Jose 2026-09-24: configurable)
  useEffect(() => {
    if (!OPERATOR_PIN_ENFORCED || !isSharedShopAccount || !currentBarber || (currentBarber as any)?.staySignedIn || autoLockMs <= 0) return;
    let timer: ReturnType<typeof setTimeout>;
    const reset = () => { clearTimeout(timer); timer = setTimeout(() => {
      // Work in flight (data import / checkout / any long op) → don't yank the operator mid-operation
      // (that was clearing the session during imports → "different Firestore instance" crash). Re-arm
      // and re-check after another full interval. (2026-10-06)
      if (pinLockService.isBusy()) { reset(); return; }
      localStorage.removeItem(STATION_BARBER_KEY);
      pinLockService.setCurrentBarber(null);
      setCurrentBarber(null); // → derived effect shows the PIN pad for the next operator
    }, autoLockMs); };
    const evts = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    evts.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer); evts.forEach((e) => window.removeEventListener(e, reset)); };
  }, [isSharedShopAccount, currentBarber, autoLockMs]);

  // Subscribe to lock service state changes
  useEffect(() => {
    const unsubscribe = pinLockService.subscribe(setIsLocked);
    return () => unsubscribe();
  }, []);

  // On the SHARED shop account, pinLockService.lock() (fires on screen-lock/app-wake via
  // visibilitychange) must NOT surface a barber-verify pad — it should release to the operator PIN
  // pad so the next person taps in. Convert any lock into "clear operator" → derived effect shows
  // the single operator pad immediately. (Jose 2026-09-24: kills the second pad.)
  useEffect(() => {
    if (!OPERATOR_PIN_ENFORCED || !isSharedShopAccount || !isLocked || !currentBarber) return;
    localStorage.removeItem(STATION_BARBER_KEY);
    pinLockService.setCurrentBarber(null);
    pinLockService.unlock();
    setCurrentBarber(null);
  }, [isLocked, isSharedShopAccount, currentBarber]);

  // Set barber and their PIN settings (saves to localStorage)
  const setBarber = useCallback((barber: BarberPinSettings | null) => {
    setCurrentBarber(barber);
    pinLockService.setCurrentBarber(barber);

    if (barber) {
      localStorage.setItem(STATION_BARBER_KEY, JSON.stringify(barber));
      console.log('[PinLock] Station barber set:', barber.barberName);
      // PUSH ROUTING (Jose 2026-10-24): re-tag THIS device's push token to the barber who just tapped in,
      // so the operator-alert Cloud Function pushes their arrivals here. Silent (only if already granted).
      initBarberPush(barber.barberId, barber.barberName).catch(() => {});
    } else {
      localStorage.removeItem(STATION_BARBER_KEY);
      console.log('[PinLock] Station barber cleared');
      // Tap-out: drop this device's token so the next barber's alerts don't reach the last one.
      clearBarberPush().catch(() => {});
    }
    // requiresStationLogin is DERIVED from (shop account + no operator) — the effect updates it.
  }, []);

  // Clear the acting operator. On the shared shop account (staging) the derived effect then shows the
  // PIN pad for the next operator; on an owner's own login it just drops the operator, no pad.
  const signOutStation = useCallback(() => {
    setCurrentBarber(null);
    pinLockService.setCurrentBarber(null);
    localStorage.removeItem(STATION_BARBER_KEY);
    // requiresStationLogin is DERIVED (shop account + no operator) — the effect updates it.
    setIsLocked(false);
    pinLockService.unlock();
    console.log('[PinLock] Cleared station operator');
  }, []);

  // Load barber settings by ID
  const loadBarberById = useCallback(async (barberId: string) => {
    const settings = await pinLockService.loadBarberSettings(barberId);
    if (settings) {
      setBarber(settings);
    }
  }, [setBarber]);

  // Lock the screen
  const lock = useCallback(() => {
    pinLockService.lock();
  }, []);

  // Unlock the screen
  const unlock = useCallback(() => {
    pinLockService.unlock();
  }, []);

  // Record activity (resets auto-lock timer)
  const recordActivity = useCallback(() => {
    pinLockService.recordActivity();
  }, []);

  // "Tap in to manage pay" (Jose 2026-11-04): a pay surface is locked → surface the operator PIN pad so they
  // re-elevate (re-mints the in-memory operator session). Also kick an authoritative re-read for an owner
  // already on their own main session (no pad needed there — the read simply retries).
  const promptForPay = useCallback(() => {
    setForcePinPad(true);
    barberDataService.ensurePrivatePayLoaded().catch(() => {});
  }, []);

  // Handle successful PIN entry
  const handleUnlock = useCallback(() => {
    setIsLocked(false);
  }, []);

  // Handle logout from lock screen
  const handleLogout = useCallback(() => {
    setBarber(null);
    if (onLogout) {
      onLogout();
    }
  }, [setBarber, onLogout]);

  // Handle station login from PIN screen
  const handleStationLogin = useCallback((barber: Barber) => {
    const barberSettings: BarberPinSettings = {
      barberId: barber.id,
      barberName: `${barber.firstName || ''} ${barber.lastName || ''}`.trim() || (barber as any).name || 'Barber',
      hasPin: (barber as any).hasPinLogin ?? true, // just verified a PIN server-side → they have one
      requirePinOnWake: (barber as any).staySignedIn ? false : (barber.requirePinOnWake ?? true), // "Stay signed in" → no wake lock
      requirePinOnOpen: false, // IMPORTANT: Don't lock immediately - PIN was just verified!
      autoLogoutMinutes: (barber as any).staySignedIn ? 0 : (barber.autoLogoutMinutes || 30),
      staySignedIn: !!(barber as any).staySignedIn, // owner/manager opt-out of the PIN pad (Jose 2026-10-19)
      // Carry authority on the session so usePermission is correct the instant they tap in (Jose 2026-10-21).
      role: (barber as any).role,
      accessLevel: (barber as any).accessLevel,
      permissions: (barber as any).permissions,
    };
    setBarber(barberSettings);
    setForcePinPad(false); // re-elevation (or first tap-in) satisfied → drop the forced pad
    // Make sure screen is unlocked after station login
    setIsLocked(false);
    pinLockService.unlock();
    // 🧾 Shop Account audit (Jose 2026-08-09): record WHO logged in, at WHICH station (device tag),
    // WHEN — the movement/use trail. Best-effort; never blocks sign-in. See stationService.
    stationService.logStationLogin({ barberId: barberSettings.barberId, barberName: barberSettings.barberName });
    // (Clock-in/attendance/geofence removed 2026-09-01 per Jose — PIN is identity only, not hourly.
    // The station-login above is the sole "who's at this iPad" record. See SLIK-STATION-CLOCKIN-RESEARCH.)
  }, [setBarber]);

  const value: PinLockContextType = {
    isLocked,
    currentBarber,
    requiresStationLogin,
    setBarber,
    loadBarberById,
    lock,
    unlock,
    recordActivity,
    signOutStation,
    promptForPay,
  };

  // iOS/WKWebView REPAINT NUDGE (Jose 2026-10-25): when the full-screen PIN/lock overlay DISMISSES, the
  // region it was covering (notably the Operations Calendar/Appointments/Waitlist tab strip) can be left
  // on a STALE BLANK compositing layer on the shared iPads — it looks "gone" until a reload, while the
  // live-updating calendar keeps repainting. On the overlay's shown→hidden edge, fire a resize event
  // (double-rAF) to force a re-layout/repaint of the previously-covered content. No remount, no data loss.
  const overlayShown = requiresStationLogin
    || (!requiresStationLogin && isLocked && !!currentBarber && !(OPERATOR_PIN_ENFORCED && isSharedShopAccount));
  const prevOverlayShown = useRef(false);
  useEffect(() => {
    if (prevOverlayShown.current && !overlayShown) {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        try { window.dispatchEvent(new Event('resize')); } catch { /* best-effort repaint nudge */ }
      }));
    }
    prevOverlayShown.current = overlayShown;
  }, [overlayShown]);

  // Don't render anything until initialized
  if (!isInitialized) {
    return (
      <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-purple-900 via-indigo-900 to-indigo-900 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <PinLockContext.Provider value={value}>
      {children}

      {/* Operator PIN pad — shows on the shared shop account until someone taps in. */}
      {requiresStationLogin && (
        // No email-login bypass (Jose 2026-09-27): owners PIN in (their PIN resolves to owner). The
        // bypass was a barber→login/create-account path. A full shop logout is manager+ only, from the
        // in-app sidebar "Switch Account".
        <StationLoginScreen
          onLogin={handleStationLogin}
          shopName="SLIK Barber Lounge"
        />
      )}

      {/* Pin Lock Screen - Shows on inactivity (already know the barber).
          NOT on the shared shop account: there, screen-lock/idle must RELEASE to the operator PIN
          pad (StationLoginScreen) so the NEXT person taps in — a barber-specific "verify Edgar" lock
          would surface as a wrong SECOND pad (Jose 2026-09-24: two pads, one "expects Edgar", one
          "Diana works"). On the shared account the idle timer clears the operator → operator pad. */}
      {!requiresStationLogin && isLocked && currentBarber && !(OPERATOR_PIN_ENFORCED && isSharedShopAccount) && (
        <PinLockScreen
          onUnlock={handleUnlock}
          onLogout={onLogout ? handleLogout : undefined}
          // Re-derive the operator name FRESH from the live barber record (Jose 2026-08-10) — the stored
          // currentBarber.barberName in localStorage can be a stale snapshot ("Jose mat" vs "Jose matos").
          barberName={(() => {
            const b = currentBarber.barberId ? barberDataService.getBarberById(currentBarber.barberId) : null;
            const fresh = b ? (`${b.firstName || ''} ${b.lastName || ''}`.trim() || (b as any).name) : '';
            return fresh || currentBarber.barberName || 'Barber';
          })()}
        />
      )}
    </PinLockContext.Provider>
  );
};

/**
 * Hook to access PIN lock context
 */
export const usePinLock = (): PinLockContextType => {
  const context = useContext(PinLockContext);
  if (context === undefined) {
    throw new Error('usePinLock must be used within a PinLockProvider');
  }
  return context;
};

export default PinLockContext;
